import { Typography, Grid, Button, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicantInfo } from '../../../shared/VolunteerApplicationServiceTypes';
import { useWizard } from '../../../components/wizard/useWizard';
import { useApplicantInterviewWizard } from './useApplicantInterviewWizard';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import { useEffect } from 'react';
import { APPLICANT_INTERVIEW, INTERVIEW_LANDING } from '../../../shared/Routes';
import { ApplicantDetailsTable } from '../ApplicantDetailsTable';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import RightArrowWhite from '../../../assets/images/arrow-right-white.svg';

const { Text } = Typography;
const { useBreakpoint } = Grid;
interface LandingPageProps {
	applicantInfo?: ApplicantInfo;
}

const SectionHeader = ({ heading }: { heading: string }) => {
	const { t } = useTranslation();
	const { navigateToPage } = useInterviewLandingPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	return (
		<div className="relative flex items-center justify-between">
			{!isMobile && (
				<Button
					type="primary"
					onClick={() => navigateToPage(INTERVIEW_LANDING)}
					icon={<Icon src={LeftArrowWhite} width={'w-4'} height={'h-4'} />}
					className="absolute left-0 font-bold text-sm"
				>
					{t('back')}
				</Button>
			)}
			<Text className="flex-grow text-center font-sans font-semibold text-lg text-secondary-black my-4">
				{heading}
			</Text>
		</div>
	);
};

export const LandingPage = ({ applicantInfo }: LandingPageProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { navigateToPage } = useInterviewLandingPage();

	return (
		<div className="flex flex-col h-full min-h-0">
			<SectionHeader heading={t('candidateInterview')} />

			<ApplicantDetailsTable applicantInfo={applicantInfo} />

			<div className="flex justify-center">
				<div
					className={`flex flex-col items-center bg-white p-4 rounded-lg shadow-md my-4 ${
						!isMobile ? 'w-3/4' : 'w-full'
					}`}
				>
					<Text className="text-base font-semibold text-center mt-2 text-secondary-black">
						{t('setContextPolitely')}
					</Text>
					<Divider className="border border-solid border-gray-200" />
					<div className="font-sans font-semibold text-sm text-gray-650 px-4">
						<p className="font-extrabold">
							{t('dearProposer')}
							{','}
						</p>
						<p>{t('proposerThankYouForVolunteering')}</p>
						<p>
							<b>
								{'1. '}
								{t('schedulingInterviews')}
							</b>
							<div className="ml-4">
								<li>{t('avoidColdCalls')}</li>
								<li>{t('contactCandidatesToAgree')}</li>
							</div>
						</p>
						<p>
							<b>
								{'2. '}
								{t('interviewMode')}
							</b>
							<div className="ml-4">
								<li>{t('conductAllInterviews')}</li>
								<li>{t('contactAreaCoordToReassign')}</li>
							</div>
						</p>
						<p>
							<b>
								{'3. '}
								{t('introScript')}
							</b>
							<p className="mt-1">{t('useTheFollowing')}</p>
							<p>
								{'"'}
								{t('applicantInterviewMayITalkToYou')}
								{'"'}
							</p>
						</p>
						<p>
							<b>
								{'4. '}
								{t('contextSetting')}
							</b>
							<p className="mt-1">{t('startOfTheInterview')}</p>
							<p>
								{'"'}
								{t('thisConversationIsImportantPartOfProcess')}
								{'"'}
							</p>
						</p>
						<p>
							<b>
								{'5. '}
								{t('documentResponses')}
							</b>
							<div className="ml-4">
								<li>{t('documentTheCandidatesResponse')}</li>
								<li>{t('maintainObjectivity')}</li>
							</div>
						</p>
						<p>{t('ourCollectiveResponsibility')}</p>
					</div>
					<div
						className={`flex ${
							!isMobile ? 'justify-end' : 'justify-center'
						} w-full gap-2 mt-4`}
					>
						<Button
							onClick={() => navigateToPage(INTERVIEW_LANDING)}
							className="font-bold text-sm"
						>
							{t('cancel')}
						</Button>
						<Button
							type="primary"
							onClick={() => navigateToPage(APPLICANT_INTERVIEW)}
							className="font-bold text-sm"
						>
							{t('next')}{' '}
							<Icon src={RightArrowWhite} width={'w-5'} height={'h-5'} />
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export const LandingPageContainer = () => {
	const { setNextButtonDisabled } = useWizard();

	const { application } = useApplicantInterviewWizard();

	useEffect(() => {
		if (!application?.preceptorReference) {
			setNextButtonDisabled(true);
		}
	}, [application?.preceptorReference, setNextButtonDisabled]);

	return <LandingPage applicantInfo={application?.applicant} />;
};
